import React from 'react';
import { graphql } from 'gatsby';
import Image from '../../primitives/image';
import Container from '../../primitives/grid/container';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { colors } from '../../primitives/colors';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';
import NotchedSlider, { NotchedSliderInstance } from '../../primitives/notched-slider';
import { breakpoints } from '../../primitives/tokens';
import Typography from '../../primitives/typography';
import Divider from '../../primitives/divider';
import Pagination from '../../primitives/notched-slider/pagination';
import RowSet from '../../primitives/grid/row-set';

const IconBlurbsSlider: React.FC<Queries.IconBlurbsSliderDataFragment> = (data) => {
    const { heading, subHeadline, settings, slides } = data;

    if (!settings) return null;

    const { darkMode, iconOnLeft, numberOfSlides } = settings;
    const slidesPerViewDesktop = numberOfSlides || 3;

    return (
        <NotchedSlider slideCount={(slides || []).length}>
            <section css={[{
                padding: '48px 0 0',
                overflow: 'hidden',
                background: darkMode ? colors.shadesBlack : undefined,
            }]}
            >
                <Container css={{
                    position: 'relative',
                    paddingTop: '4px',
                }}
                >
                    {(heading || subHeadline) && (
                        <div css={{ marginBottom: '32px' }}>
                            <RowSet breakpoints={{
                                dt: { between: 16 },
                            }}
                            >
                                <Typography
                                    as="h2"
                                    fontWeight="regular"
                                    reducedLetterSpacing
                                    fontSize="3xl"
                                    css={[{
                                        color: darkMode ? colors.primaryOrange : colors.shadesBlack,
                                        [breakpoints.tb]: {
                                            textAlign: 'center',
                                            maxWidth: '80%',
                                            margin: 'auto',
                                        },
                                    }]}
                                >
                                    {apCaseOnlyTitleTags(heading)}
                                </Typography>
                                {!!subHeadline && (
                                    <Typography
                                        fontSize="xl"
                                        css={[RichTextEditorStyles, {
                                            color: darkMode ? colors.shadesWhite : undefined,
                                            marginTop: '4px',
                                            lineHeight: '28px',
                                        }]}
                                        dangerouslySetInnerHTML={{
                                            __html: apCaseOnlyTitleTags(subHeadline),
                                        }}
                                    />
                                )}
                            </RowSet>
                        </div>
                    )}
                </Container>
                <Divider css={{
                    opacity: darkMode ? 0.1 : 0.6,
                }}
                />
                {slides && (
                    <NotchedSliderInstance slidesPerView={{
                        dt: slidesPerViewDesktop,
                        tb: 1,
                    }}
                    >
                        <Container css={{
                            position: 'relative',
                            padding: '48px 0',
                            [breakpoints.tb]: {
                                padding: '18px 0',
                                minHeight: 'calc(100% + 32px)',
                            },
                        }}
                        >
                            <div css={{
                                position: 'absolute',
                                height: '100%',
                                width: `calc(50vw - ${1440 / 2}px - 32px)`,
                                background: `linear-gradient(90deg, transparent, ${darkMode ? colors.shadesBlack : colors.shadesWhite})`,
                                right: '0px',
                                top: '0px',
                                transform: 'translateX(100%)',
                                zIndex: 2,
                                pointerEvents: 'none',
                                [breakpoints.tb]: { display: 'none' },
                            }}
                            />
                            <div css={{
                                position: 'absolute',
                                height: '100%',
                                width: `calc(50vw - ${1440 / 2}px - 32px)`,
                                background: `linear-gradient(-90deg, transparent, ${darkMode ? colors.shadesBlack : colors.shadesWhite})`,
                                left: '0px',
                                top: '0px',
                                transform: 'translateX(-100%)',
                                zIndex: 2,
                                pointerEvents: 'none',
                                [breakpoints.tb]: { display: 'none' },
                            }}
                            />

                            <NotchedSlider.Slides overflowVisible masksLeft masksRight>
                                {slides.map((slide) => {
                                    if (!slide) return null;
                                    const {
                                        copy,
                                        heading: slideHeading,
                                        icon,
                                    } = slide;

                                    return (
                                        <Col
                                            key={slide.heading}
                                            breakpoints={{ dt: { span: 6 }, tb: { span: 3 }, mb: { span: 2 } }}
                                            css={{
                                                position: 'relative',
                                                paddingTop: slideHeading ? '24px' : '0',
                                                paddingBottom: slideHeading ? '24px' : '0',
                                                paddingRight: '48px',
                                                height: '100%',
                                                '&:after': {
                                                    right: '24px',
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '0',
                                                    height: '100%',
                                                    backgroundColor: colors.grey200,
                                                    opacity: darkMode ? 0.2 : 1,
                                                    width: '1px',
                                                },
                                                '&:nth-of-type(4n):after': {
                                                    display: 'none',
                                                },
                                                [breakpoints.tb]: {
                                                    paddingRight: 0,
                                                    minHeight: 'calc(100% + 32px)',
                                                    '&:after': { right: '-8px' },
                                                    '&:nth-of-type(2n):after': { display: 'none' },
                                                },
                                            }}
                                        >
                                            <div css={{
                                                color: darkMode ? colors.shadesWhite : undefined,
                                                display: 'flex',
                                                gap: '10px',
                                                flexDirection: iconOnLeft ? 'row' : 'column',
                                                [breakpoints.tb]: {
                                                    flexDirection: 'column',
                                                    gap: '16px',
                                                    padding: '0 18px',
                                                },
                                            }}
                                            >
                                                {!!icon?.url && (
                                                    <div css={{
                                                        maxWidth: '40px',
                                                        minWidth: '40px',
                                                        marginBottom: iconOnLeft ? '8px' : '4px',
                                                        marginLeft: iconOnLeft ? '0' : '-7px',
                                                        marginRight: '16px',
                                                        marginTop: '12px',
                                                        [breakpoints.tb]: {
                                                            maxWidth: '50px',
                                                            minWidth: '50px',
                                                            margin: 0,
                                                            paddingTop: '12px',
                                                            paddingRight: '16px',
                                                            paddingBottom: iconOnLeft ? '8px' : '4px',
                                                        },
                                                    }}
                                                    >
                                                        <Image image={icon} alt={icon.title || ''} />
                                                    </div>
                                                )}
                                                <div
                                                    css={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        paddingTop: '4px',
                                                    }}
                                                >
                                                    {slideHeading && (
                                                        <Typography
                                                            fontSize="xl"
                                                            as="h3"
                                                            css={{
                                                                marginBottom: '8px',
                                                                fontWeight: 600,
                                                                color: darkMode ? colors.shadesWhite : undefined,
                                                                [breakpoints.tb]: {
                                                                    marginBottom: 0,
                                                                    paddingBottom: '8px',
                                                                },
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: slideHeading,
                                                            }}
                                                        />
                                                    )}
                                                    <div
                                                        css={RichTextEditorStyles}
                                                        // eslint-disable-next-line react/no-danger
                                                        dangerouslySetInnerHTML={{
                                                            __html: copy || '',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </NotchedSlider.Slides>

                        </Container>
                        <Divider css={{
                            opacity: darkMode ? 0.1 : 0.6,
                        }}
                        />
                        <Container css={{
                            position: 'relative',
                        }}
                        >
                            <Row>
                                <Col breakpoints={{
                                    dt: { start: 4, span: 4 },
                                    mb: { start: 0, span: 4 },
                                }}
                                >
                                    <Pagination />
                                </Col>
                            </Row>
                        </Container>
                    </NotchedSliderInstance>
                )}
            </section>
        </NotchedSlider>
    );
};

export default IconBlurbsSlider;

export const query = graphql`
    fragment IconBlurbsSliderData  on CsIconBlurbsGroup {
        heading
        subHeadline
        spacing {
            topGutter
            bottomGutter
        }
        settings {
            darkMode
            iconOnLeft
            numberOfSlides
        }
        slides {
            heading
            copy
            icon {
                filename
                title
                url
                contentStackAsset(width: 100)
            }
        }
    }
`;
